import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';
import IconMinus from '../../../Icons/IconMinus';
import IconPlus from '../../../Icons/IconPlus';

/**
 * GuestCounterControls displays increment/decrement controls for guest selection.
 * Can be used standalone or inside controlled inputs.
 *
 * @param {object} props
 * @param {Array<{name: string, label: string, infoLabel?: string, minimum: number, maximum?: number, excludeFromTotal?: boolean}>} props.guestOptions - Guest type options
 * @param {object} props.value - Current guest count values { adult: number, child: number, ... }
 * @param {(newValue: object) => void} props.onChange - Callback when values change
 * @param {number} [props.totalMaximum] - Combined maximum across all options not marked excludeFromTotal
 */
const GuestCounterControlsComponent = ({
  guestOptions,
  value = {},
  onChange,
  totalMaximum,
}) => {
  const currentTotal = totalMaximum != null
    ? guestOptions.reduce((sum, opt) => (opt.excludeFromTotal ? sum : sum + (value[opt.name] || 0)), 0)
    : null;
  const handleDecrement = (optionName) => {
    const newValue = {
      ...value,
      [optionName]: value[optionName] - 1,
    };
    onChange(newValue);
  };

  const handleIncrement = (optionName) => {
    const newValue = {
      ...value,
      [optionName]: value[optionName] + 1,
    };
    onChange(newValue);
  };

  return (
    <Stack spacing={2}>
      {guestOptions.map((option) => (
        <Stack direction="row" justifyContent="space-between" key={option.name}>
          <Stack direction="column" spacing={0}>
            <Typography variant="body2">{option.label}</Typography>
            {option.infoLabel ? (
              <Typography color="text.secondary" variant="caption">
                {option.infoLabel}
              </Typography>
            ) : null}
          </Stack>
          <Stack alignItems="center" direction="row" spacing={2}>
            <IconButton
              disabled={value[option.name] <= option.minimum}
              onClick={() => handleDecrement(option.name)}
            >
              <IconMinus />
            </IconButton>
            <Typography component="span" variant="h6">
              {value[option.name]}
            </Typography>
            <IconButton
              disabled={
                (option.maximum != null && value[option.name] >= option.maximum)
                || (currentTotal != null && !option.excludeFromTotal && currentTotal >= totalMaximum)
              }
              onClick={() => handleIncrement(option.name)}
            >
              <IconPlus />
            </IconButton>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

GuestCounterControlsComponent.displayName = 'GuestCounterControls';

const GuestCounterControls = memo(GuestCounterControlsComponent);

export default GuestCounterControls;
